import PropTypes from 'prop-types'
import { NumericFormat } from 'react-number-format'
import { useFieldState } from 'hooks/useFieldState'
import { useFieldFocus } from 'context/FieldFocusContext'
import { InputAdornment, TextField } from '@mui/material'
import { Check, ErrorOutline } from '@mui/icons-material'
import { useState, useRef, useEffect } from 'react'

const MuiFloatField = ({
  id,
  isCurrency = false,
  decimalScale = 2,
  value,
  label,
  showAdornment = false,
  placeholder,
  onChange,
  shrink,
  enableHelpText,
  onBlur = (e) => console.log(e),
  helpText,
  name,
  ...rest
}) => {
  const [rawValue, setRawValue] = useState(value)
  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  const inputRef = useRef(null)
  const fieldId = id || name || label

  useEffect(() => {
    if (enableHelpText && fieldId) {
      registerHelpField(fieldId, helpText)
    }
  }, [enableHelpText, fieldId])

  const validateCurrency = (val) => {
    if (val === '' || val == null) return true
    const pattern = `^\\$?\\d{1,3}(,\\d{3})*(\\.\\d{0,${decimalScale}})?$|^\\$?\\d+(\\.\\d{0,${decimalScale}})?$`
    const regex = new RegExp(pattern)
    return regex.test(`${val}`)
  }

  const validateFloat = (val) => {
    if (val === '' || val == null) return true
    return /^-?\d+(\.\d+)?$/.test(`${val}`)
  }

  const { isError, isSuccess, handleOnblur } = useFieldState({
    validatorFunction: (val) => {
      return isCurrency ? validateCurrency(val) : validateFloat(val)
    },
  })

  const handleOnblurWithValidation = (e) => {
    const inputVal = e.target.value
    const valid = isCurrency
      ? validateCurrency(inputVal)
      : validateFloat(inputVal)

    handleOnblur(inputVal)

    // if (valid) {
    //   onBlur(rawValue)
    // }
    // change out onblur to handle blank
    if (inputVal === '' || valid) {
      const out = inputVal === '' ? 0 : rawValue
      onBlur(out)
    }
    handleBlur()
  }

  const styles = {
    '&>label': { fontSize: '14px', color: '#75787B', top: '-2px' },
    '&>label.Mui-focused': {
      fontSize: '1rem',
      color: '#2370ED',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.Mui-focused.Mui-error': { color: '#ea0606' },
    '&>label.MuiFormLabel-filled, &>label.MuiInputLabel-shrink': {
      fontSize: '1rem',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.MuiFormLabel-filled.Mui-disabled, &>label.MuiFormLabel-root.Mui-disabled':
      {
        top: 0,
        background:
          'linear-gradient(to bottom, #fff 0%, #fff 50%, #e9ecef 50%, #e9ecef 100%)',
      },
    '&>div.MuiInputBase-root': {
      padding: '0!important',
      borderRadius: '4px !important',
    },
    '&>div.MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#e9ecef!important',
    },
    '&>div.MuiInputBase-root.Mui-focused': {
      boxShadow: '0 0 0 1.5px #2370ED',
    },
    '&>div>.MuiInputBase-input': {
      width: '100%!important',
      padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
      display: 'flex',
    },
    '&>div.MuiInputBase-root>svg': {
      display: 'block',
      height: '1.5em',
      width: '1.5em',
      top: 'calc(50% - 0.75em)',
    },
    '&.linkedDisabled>div.MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#F5EBFE !important',
      border: '1px solid #8F62F8',
    },
    '&.linkedDisabled>label.MuiFormLabel-filled.Mui-disabled': {
      background:
        'linear-gradient(to bottom, #fff 0%, #fff 50%, #F5EBFE 50%, #F5EBFE 100%)',
      color: '#8F62F8',
    },
    '&.linkedDisabled>div>.MuiInputBase-input, &.linkedDisabled>label.MuiFormLabel-root.Mui-disabled':
      {
        color: '#8F62F8',
        WebkitTextFillColor: '#8F62F8',
      },
  }

  // Determine if we should use fixedDecimalScale
  // Only use it if the value has non-zero decimals (e.g., 1234.90)
  // Don't use it for whole numbers (e.g., 1234)
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  const hasDecimalValue =
    numValue != null &&
    !Number.isNaN(numValue) &&
    Number.isFinite(numValue) &&
    !Number.isInteger(numValue)

  return (
    <NumericFormat
      label={label}
      value={value}
      placeholder={placeholder}
      prefix={isCurrency ? '$' : ''}
      thousandSeparator={isCurrency}
      fixedDecimalScale={hasDecimalValue}
      decimalScale={decimalScale}
      inputProps={{ inputMode: 'numeric' }}
      InputProps={{
        endAdornment: showAdornment && (
          <InputAdornment position="end">
            {isError ? (
              <ErrorOutline
                color="error"
                sx={{ fontSize: '24px !important' }}
              />
            ) : isSuccess ? (
              <Check color="success" sx={{ fontSize: '24px !important' }} />
            ) : null}
          </InputAdornment>
        ),
      }}
      InputLabelProps={shrink !== undefined ? { shrink } : undefined}
      sx={styles}
      customInput={TextField}
      inputRef={inputRef}
      fullWidth
      onValueChange={(values) => {
        let safeValue
        if (values.value === '') {
          safeValue = 0 // Clearing the input submits 0
        } else if (values.floatValue != null) {
          safeValue = Math.abs(values.floatValue)
        }
        setRawValue(safeValue)
        onChange && onChange(safeValue)
      }}
      onFocus={(e) => {
        handleFocus(fieldId, e.target)
      }}
      onBlur={(e) => {
        handleOnblurWithValidation(e)
        handleBlur()
      }}
      onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
      onMouseLeave={(e) => {
        if (document.activeElement !== e.currentTarget) {
          handleBlur()
        }
      }}
      {...rest}
    />
  )
}

MuiFloatField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  isCurrency: PropTypes.bool,
  decimalScale: PropTypes.number,
  showAdornment: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  shrink: PropTypes.bool,
  enableHelpText: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
}

export default MuiFloatField
